import { create } from 'zustand'

/**
 * The Point an admin has chosen to work at, shared across every
 * point-scoped section (Сводка, Манифесты, Выдача, Касса смены, Хранение и сроки).
 *
 * It exists because the Point is a property of **where the person is standing**,
 * not of the screen they happen to have open: someone working the counter at
 * Чиланзар is at Чиланзар whether they are receiving a manifest, handing a parcel
 * over or counting the drawer. Making each screen ask again was pure friction, and
 * worse, it let two screens disagree about which shelf was being looked at.
 *
 * Deliberately **not persisted**. The Point is session state, and a stale one
 * restored from a previous session is a real hazard: an operator who opens the
 * panel the next morning and sees another Point's shelf without having chosen it
 * is being invited to work the wrong shelf. Picking it once per session is cheap;
 * silently defaulting it is not. (Adding `persist` here is a one-line change if
 * that trade is ever re-decided.)
 *
 * Only an admin's choice lives here. A bound role never picks: `useLocationScope`
 * reads its place from the signed-in account (ADR-0029) and ignores this store.
 */
interface SelectedLocationState {
	/** The chosen Location id, or `null` when nothing is chosen yet. */
	locationId: string | null
	setLocationId: (locationId: string | null) => void
	/** Forgets the choice — called on logout, so a role switch starts clean. */
	clearLocation: () => void
}

export const useSelectedLocationStore = create<SelectedLocationState>()((set) => ({
	locationId: null,
	setLocationId: (locationId) => set({ locationId }),
	clearLocation: () => set({ locationId: null }),
}))
